import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';

interface EmbedHeaderProps {
   title: string;
   onRailToggle: () => void;
   onPrev: () => void;
   onNext: () => void;
   canPrev: boolean;
   canNext: boolean;
   // Video-only guides have no steps: no rail toggle, no prev/next — just the
   // title and fullscreen (see .step-header-no-nav in the vanilla layout).
   showNav: boolean;
   // Whether the rail toggle renders at all (guides with a step list).
   showRailToggle: boolean;
   // The rail is docked as a left column ≥ breakpoint: shift the fixed header
   // over so it starts beside the rail rather than under it.
   railDocked: boolean;
   // How far through the guide, 0–100. The rail carries the same reading, but it
   // collapses and is a drawer on mobile, so the bar lives here to always show.
   percent: number;
   onFullscreen?: () => void;
   fullscreenSupported: boolean;
   isFullscreen?: boolean;
}

const navButton = 'button-ghost min-h-11 min-w-11 rounded-lg text-gray-700 hover:text-gray-950';

/**
 * Fixed top bar shared by the intro, step, and conclusion screens: rail toggle,
 * the reader's position, fullscreen, prev/next, and a progress hairline along
 * its bottom edge.
 */
export function EmbedHeader({
   title,
   onRailToggle,
   onPrev,
   onNext,
   canPrev,
   canNext,
   showNav,
   showRailToggle,
   railDocked,
   percent,
   onFullscreen,
   fullscreenSupported,
   isFullscreen,
}: EmbedHeaderProps) {
   return (
      <header
         className={cn(
            'fixed right-0 top-0 z-20 border-b border-gray-200 bg-white transition-[left] duration-200 motion-reduce:transition-none',
            railDocked ? 'left-0 md:left-[260px]' : 'left-0'
         )}
      >
         <div className="flex items-center gap-1 px-2 py-1 sm:gap-2 sm:px-3">
            {showRailToggle && (
               <button
                  type="button"
                  className={navButton}
                  onClick={onRailToggle}
                  aria-label={_js('Toggle steps')}
               >
                  <FaIcon name="bars" />
               </button>
            )}

            <h2 className="m-0 min-w-0 flex-1 truncate text-base font-semibold text-gray-950">
               {title}
            </h2>

            {fullscreenSupported && (
               <button
                  type="button"
                  className={cn(navButton, 'max-[719px]:hidden')}
                  onClick={onFullscreen}
                  aria-label={isFullscreen ? _js('Exit fullscreen') : _js('Fullscreen')}
                  aria-pressed={isFullscreen}
               >
                  <FaIcon name={isFullscreen ? 'compress' : 'expand'} />
               </button>
            )}

            {showNav && (
               <div className="flex items-center">
                  <button
                     type="button"
                     className={navButton}
                     onClick={onPrev}
                     disabled={!canPrev}
                     aria-label={_js('Previous step')}
                     title={_js('Hint: You can use left/right arrow keys to navigate the guide!')}
                  >
                     <FaIcon name="arrow-left" />
                  </button>
                  <button
                     type="button"
                     className={navButton}
                     onClick={onNext}
                     disabled={!canNext}
                     aria-label={_js('Next step')}
                     title={_js('Hint: You can use left/right arrow keys to navigate the guide!')}
                  >
                     <FaIcon name="arrow-right" />
                  </button>
               </div>
            )}
         </div>

         {/* Decorative: the position it reports is already spelled out in the title. */}
         {showNav && (
            <div className="absolute inset-x-0 bottom-0 h-0.5" aria-hidden>
               <div
                  className="h-full bg-brand-500 transition-[width] duration-300 ease-out motion-reduce:transition-none"
                  style={{ width: `${percent}%` }}
               />
            </div>
         )}
      </header>
   );
}
